import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPopper } from '@popperjs/core';
import styled from 'styled-components';
import { color } from 'styled-system';
import deepmerge from 'deepmerge';
import { Portal } from '../Portal/Portal.es.js';
import { Box } from '../Box/Box.es.js';
import '../../utils/responsive.es.js';
import 'body-scroll-lock';
import '../../hooks/useNotification/useNotification.es.js';
import '../../hooks/useToast/useToast.es.js';
import { useOnClickOutside } from '../../hooks/useOnClickOutside/useOnClickOutside.es.js';
import '../../utils/assertions.es.js';
import { warn } from '../../utils/functions.es.js';
import { mergeRefs } from '../../utils/refs.es.js';
import '../../utils/scrollIntoView.es.js';
import 'polished';
import '@styled-system/css';
import '../../utils/useEnhancedEffect.es.js';
import 'react-is';
import 'react-beautiful-dnd';
import 'nanoid/non-secure';
import { forwardRef } from '../../system/forwardRef.es.js';
import { TransitionBase } from '../TransitionBase/TransitionBase.es.js';
import { matchWidth } from './modifiers.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const Arrow = styled("span").attrs(() => ({
  "data-popper-arrow": true,
  role: "presentation"
}))`
    --popper-arrow-size: ${({ size = 8 }) => `${size}px`};

    visibility: hidden;

    &,
    &::before {
        position: absolute;
        width: var(--popper-arrow-size, 8px);
        height: var(--popper-arrow-size, 8px);
        background-color: inherit;
    }

    &::before {
        inset: 0;
        visibility: visible;
        content: '';
        transform: rotate(45deg);
        ${color};
    }

    [data-popper-placement^='top'] & {
        bottom: calc(var(--popper-arrow-size) / -2);
    }

    [data-popper-placement^='bottom'] & {
        top: calc(var(--popper-arrow-size) / -2);
    }

    [data-popper-placement^='left'] & {
        right: calc(var(--popper-arrow-size) / -2);
    }

    [data-popper-placement^='right'] & {
        left: calc(var(--popper-arrow-size) / -2);
    }
`;
const defaultPopperOptions = {
  placement: "auto",
  modifiers: [
    {
      name: "offset",
      options: {
        offset: [0, 8]
      }
    }
  ],
  strategy: "absolute"
};
const PopperRoot = forwardRef((props, forwardedRef) => {
  const _a = props, {
    children,
    anchorRef,
    triggerRef,
    isOpen,
    onClose,
    onOpen,
    matchWidth: matchWidth$1,
    popperOptions: popperOptionsProp = {},
    transitionComponent: TransitionComponent = TransitionBase,
    disablePortal,
    usePortal = true,
    onClickOutside,
    isLazy = true,
    keepMounted = false,
    zIndex = 1
  } = _a, rest = __objRest(_a, [
    "children",
    "anchorRef",
    "triggerRef",
    "isOpen",
    "onClose",
    "onOpen",
    "matchWidth",
    "popperOptions",
    "transitionComponent",
    "disablePortal",
    "usePortal",
    "onClickOutside",
    "isLazy",
    "keepMounted",
    "zIndex"
  ]);
  warn({
    condition: disablePortal !== void 0,
    message: "disablePortal prop is deprecated and will be removed in v1, use usePortal instead"
  });
  const popupRef = useRef(null);
  const popperRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const popperOptions = deepmerge(__spreadValues(__spreadValues({}, defaultPopperOptions), {
    modifiers: [__spreadProps(__spreadValues({}, matchWidth), { enabled: !!matchWidth$1 })]
  }), popperOptionsProp);
  const handleOpen = useCallback(() => {
    if (!anchorRef.current || !popupRef.current || isLazy && !isOpen) {
      return;
    }
    if (popperRef.current) {
      popperRef.current.destroy();
      popperRef.current = null;
    }
    const popper = createPopper(anchorRef.current, popupRef.current, popperOptions);
    popperRef.current = popper;
    popperRef.current.forceUpdate();
    setHasBeenOpened(true);
    onOpen == null ? void 0 : onOpen();
  }, [anchorRef, isLazy, isOpen, popperOptions, onOpen]);
  const handleClose = useCallback(() => {
    if (!popperRef.current) {
      return;
    }
    popperRef.current.destroy();
    popperRef.current = null;
    onClose == null ? void 0 : onClose();
  }, [onClose]);
  const handleOutsideClick = (event) => {
    if (!isOpen) {
      return;
    }
    if (onClickOutside) {
      onClickOutside(event);
    }
  };
  const handleEnter = () => {
    setIsMounted(true);
  };
  const handleExited = () => {
    setIsMounted(false);
    handleClose();
  };
  useEffect(() => {
    if (popperRef.current) {
      popperRef.current.update();
    }
  });
  useEffect(() => {
    handleOpen();
  }, [handleOpen]);
  useEffect(() => {
    return () => {
      handleClose();
    };
  }, [handleClose]);
  useOnClickOutside([anchorRef, popupRef, ...triggerRef ? [triggerRef] : []], handleOutsideClick);
  const transitionComponentProps = {
    in: isOpen,
    onEnter: handleEnter,
    onExited: handleExited
  };
  if (isLazy) {
    if (!isOpen && !isMounted) {
      if (!keepMounted || keepMounted && !hasBeenOpened) {
        return null;
      }
    }
  }
  return /* @__PURE__ */ React.createElement(Portal, {
    isDisabled: disablePortal || !usePortal,
    zIndex
  }, /* @__PURE__ */ React.createElement(Box, __spreadValues({
    ref: mergeRefs(popupRef, forwardedRef),
    sx: {
      position: popperOptions.strategy,
      visibility: !isMounted ? "hidden" : "visible",
      zIndex
    }
  }, rest), /* @__PURE__ */ React.createElement(TransitionComponent, __spreadValues({}, transitionComponentProps), children)));
});
const Popper = Object.assign(PopperRoot, { Arrow });

export { Arrow, Popper };
//# sourceMappingURL=Popper.es.js.map
